import {
  Box,
  Divider,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import * as React from "react";

import {
  RobotoTableCell,
  RobotoTypography,
  RobotoDatePicker,
} from "@/shared/components";
import type { DateRange } from "@/shared/components";
import type { PerActionStats } from "@/shared/domain/admin";
import { usePerActionStats } from "@/shared/domain/admin/hooks/usePerActionStats";

import { PerActionStatsTableBody } from "./PerActionStatsTableBody";

type SortField =
  | "action_org_id"
  | "action_name"
  | "fail_count"
  | "queued_count"
  | "completed_count"
  | "success_rate";

type ColumnSort = {
  id: string; // Column name
  desc: boolean; // Whether it's ascending or descending
};

export function PerActionStats() {
  const theme = useTheme();
  const [columnSort, setColumnSort] = React.useState<ColumnSort>({
    id: "completed_count",
    desc: true,
  });

  const currentTimezone = dayjs.tz.guess();
  const today = dayjs().tz(currentTimezone);
  const yesterday = today.subtract(1, "day");

  // Initialize with full date and time, not just the day
  const [dateRange, setDateRange] = React.useState<DateRange>({
    startDate: yesterday,
    endDate: today,
  });

  // Use React Query hook for fetching data
  const {
    data: stats = [],
    isLoading,
    error,
    refetch,
  } = usePerActionStats({
    startTime: dateRange.startDate ? Math.floor(dateRange.startDate.unix()) : 0,
    endTime: dateRange.endDate ? Math.floor(dateRange.endDate.unix()) : 0,
    limit: 1000,
    enabled:
      dateRange.startDate !== null &&
      dateRange.endDate !== null &&
      dateRange.startDate.isBefore(dateRange.endDate),
  });

  // Main fetch function using current date picker values
  const fetchStats = React.useCallback(() => {
    if (
      !dateRange.startDate ||
      !dateRange.endDate ||
      dateRange.startDate.isAfter(dateRange.endDate)
    ) {
      // For validation errors, we could show a toast or handle differently
      // For now, the query will be disabled if dates are invalid
      return;
    }
    void refetch();
  }, [dateRange.startDate, dateRange.endDate, refetch]);

  // Helper function to calculate success rate
  const getSuccessRate = (stat: PerActionStats) => {
    const total = stat.completed_count + stat.fail_count;
    // Return null for cases where no actions have been attempted (completed or failed)
    // This distinguishes from 0% success rate (where actions failed)
    return total > 0 ? stat.completed_count / total : null;
  };

  // Helper function to sort stats
  const sortStats = (statsToSort: PerActionStats[]) => {
    return [...statsToSort].sort((a, b) => {
      const sortField = columnSort.id as SortField;

      if (sortField === "success_rate") {
        const aRate = getSuccessRate(a);
        const bRate = getSuccessRate(b);
        // Handle null values: null rates should be sorted to the end
        if (aRate === null && bRate === null) {
          return 0;
        }
        if (aRate === null) {
          return 1;
        }
        if (bRate === null) {
          return -1;
        }
        return columnSort.desc ? bRate - aRate : aRate - bRate;
      }

      if (sortField === "action_org_id" || sortField === "action_name") {
        const aValue = a[sortField].toLowerCase();
        const bValue = b[sortField].toLowerCase();
        return columnSort.desc
          ? bValue.localeCompare(aValue)
          : aValue.localeCompare(bValue);
      }

      return columnSort.desc
        ? b[sortField] - a[sortField]
        : a[sortField] - b[sortField];
    });
  };

  // Helper function to handle column sorting
  const handleSort = (field: SortField) => {
    if (columnSort.id === field) {
      setColumnSort({ id: field, desc: !columnSort.desc });
    } else {
      setColumnSort({
        id: field,
        desc: field !== "action_name", // action_name defaults to ascending, others to descending
      });
    }
  };

  return (
    <Box>
      <RobotoTypography variant="body1" sx={{ marginBottom: theme.spacing(2) }}>
        Per-Action Invocation Statistics
      </RobotoTypography>
      <Divider sx={{ marginBottom: theme.spacing(2) }} />

      <RobotoDatePicker
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        onDateChange={setDateRange}
        onApply={fetchStats}
        isLoading={isLoading}
        sx={{ marginBottom: theme.spacing(3) }}
      />
      {error && (
        <Paper
          variant="outlined"
          sx={{
            padding: theme.spacing(2),
            marginBottom: theme.spacing(2),
            backgroundColor: theme.palette.error.light,
          }}
        >
          <RobotoTypography
            variant="body2"
            component="pre"
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {error instanceof Error ? error.message : String(error)}
          </RobotoTypography>
        </Paper>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <RobotoTableCell
                onClick={() => handleSort("action_name")}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                  ...(columnSort.id === "action_name" && {
                    fontWeight: "bold",
                  }),
                }}
              >
                Action{" "}
                {columnSort.id === "action_name" &&
                  (columnSort.desc ? "↓" : "↑")}
              </RobotoTableCell>
              <RobotoTableCell
                align="right"
                onClick={() => handleSort("queued_count")}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                  ...(columnSort.id === "queued_count" && {
                    fontWeight: "bold",
                  }),
                }}
              >
                Queued{" "}
                {columnSort.id === "queued_count" &&
                  (columnSort.desc ? "↓" : "↑")}
              </RobotoTableCell>
              <RobotoTableCell
                align="right"
                onClick={() => handleSort("completed_count")}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                  ...(columnSort.id === "completed_count" && {
                    fontWeight: "bold",
                  }),
                }}
              >
                Completed{" "}
                {columnSort.id === "completed_count" &&
                  (columnSort.desc ? "↓" : "↑")}
              </RobotoTableCell>
              <RobotoTableCell
                align="right"
                onClick={() => handleSort("fail_count")}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                  ...(columnSort.id === "fail_count" && { fontWeight: "bold" }),
                }}
              >
                Failed{" "}
                {columnSort.id === "fail_count" &&
                  (columnSort.desc ? "↓" : "↑")}
              </RobotoTableCell>
              <RobotoTableCell
                align="right"
                onClick={() => handleSort("success_rate")}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                  ...(columnSort.id === "success_rate" && {
                    fontWeight: "bold",
                  }),
                }}
              >
                Success Rate{" "}
                {columnSort.id === "success_rate" &&
                  (columnSort.desc ? "↓" : "↑")}
              </RobotoTableCell>
            </TableRow>
          </TableHead>

          <PerActionStatsTableBody
            isLoading={isLoading}
            stats={stats}
            sortStats={sortStats}
            getSuccessRate={getSuccessRate}
          />
        </Table>
      </TableContainer>
    </Box>
  );
}
